import React from 'react';
import ChartUpIcon from 'images/dashboard/chart-up.svg';
import ChartDownIcon from 'images/dashboard/chart-down.svg';
import { getValueByPath, formatValue } from './metaUtils';
import GrowthChip from 'components/Base/Chip/GrowthChip';


export default function ChartMeta({ meta, dataContext, chartType, theme, rightExtras = null }) {
    const visible = meta?.visible !== false; // default true
    if (!meta || !visible) return null;

    // Typography helpers
    const sizeToClass = (size, fallback) => {
        switch (String(size || '').toLowerCase()) {
            case 'xs': return 'text-xs';
            case 'sm': return 'text-sm';
            case 'md':
            case 'base': return 'text-base';
            case 'lg': return 'text-lg';
            case 'xl': return 'text-xl';
            case '2xl': return 'text-2xl';
            case '3xl': return 'text-3xl';
            case '4xl': return 'text-4xl';
            default: return fallback;
        }
    };
    const weightToClassOrStyle = (weight, fallbackClass) => {
        const w = String(weight || '').toLowerCase();
        switch (w) {
            case 'light': return { className: 'font-light' };
            case 'normal':
            case 'regular': return { className: 'font-normal' };
            case 'medium': return { className: 'font-medium' };
            case 'semibold':
            case 'semi-bold': return { className: 'font-semibold' };
            case 'bold': return { className: 'font-bold' };
            case 'extrabold':
            case 'extra-bold': return { className: 'font-extrabold' };
            default: {
                // Numeric weights (100-900) -> inline style
                const numeric = Number(weight);
                if (!Number.isNaN(numeric) && numeric >= 100 && numeric <= 900) {
                    return { style: { fontWeight: numeric } };
                }
                return { className: fallbackClass };
            }
        }
    };
    const computeTypography = (cfg, defaultSizeClass, defaultWeightClass) => {
        const sizeClass = sizeToClass(cfg?.size, defaultSizeClass);
        const weightRes = weightToClassOrStyle(cfg?.weight, defaultWeightClass);
        const className = [weightRes.className || '', sizeClass].filter(Boolean).join(' ');
        const style = { ...(weightRes.style || {}), ...(cfg?.color ? { color: cfg.color } : {}) };
        return { className, style };
    };

    // Helper to resolve field configs with path/format/default and backward compatibility
    const resolveField = (cfg, defaultFormat) => {
        // Normalize simple values to { default }
        const normalizedCfg = (typeof cfg === 'string' || typeof cfg === 'number') ? { default: cfg } : (cfg || {});

        // Back-compat: support { text } and { value } as default if no explicit default/path
        const explicitPath = typeof normalizedCfg.path === 'string' ? normalizedCfg.path : undefined;
        const explicitDefault = normalizedCfg.hasOwnProperty('default') ? normalizedCfg.default
            : (normalizedCfg.hasOwnProperty('value') ? normalizedCfg.value
                : (normalizedCfg.hasOwnProperty('text') ? normalizedCfg.text : undefined));

        const raw = explicitPath !== undefined ? getValueByPath(dataContext, explicitPath) : undefined;
        const effective = (raw !== undefined && raw !== null) ? raw : explicitDefault;
        const format = normalizedCfg.format || defaultFormat;
        const prefix = normalizedCfg.prefix || '';
        const suffix = normalizedCfg.suffix || '';
        const maximumFractionDigits = normalizedCfg.maximumFractionDigits ?? 2;

        const display = formatValue(effective, format, { prefix, suffix, maximumFractionDigits });
        const hasValue = effective !== undefined && effective !== null && String(display).length > 0;
        const isVisible = normalizedCfg.visible !== false; // default true
        return { display, hasValue, isVisible };
    };

    // Primary value and label (independent visibility and defaults)
    const { display: primaryValue, hasValue: hasPrimaryValue, isVisible: showPrimaryValue } = resolveField(meta.primaryValue, meta?.primaryValue?.format || 'number');
    const { display: primaryLabel, hasValue: hasPrimaryLabel, isVisible: showPrimaryLabel } = resolveField(meta.primaryLabel, meta?.primaryLabel?.format || 'string');

    // Compute typography for primary value and label (with sensible defaults)
    const primaryValueTypos = computeTypography(meta.primaryValue, 'text-2xl', 'font-semibold');
    const primaryLabelTypos = computeTypography(meta.primaryLabel, 'text-lg', 'font-medium');

    // Delta (change percentage)
    const deltaCfg = meta.delta || {};
    const deltaRaw = deltaCfg?.path ? getValueByPath(dataContext, deltaCfg.path) : (deltaCfg?.value ?? deltaCfg?.default);
    const deltaNumber = typeof deltaRaw === 'number' ? deltaRaw : Number(deltaRaw);
    const deltaIsNumber = !Number.isNaN(deltaNumber) && deltaRaw != null;
    const isPositive = deltaCfg?.invert ? deltaNumber <= 0 : deltaNumber >= 0;
    const showDelta = deltaCfg?.visible !== false && deltaIsNumber;

    // Legend (auto or explicit)
    const legendConfig = meta.legend;

    // Attempt to auto-derive legend from normalized context and chart type
    const autoLegendItems = (() => {
        const normalized = dataContext?.normalized || {};
        const rows = Array.isArray(dataContext?.rows) ? dataContext.rows : [];
        const yKeys = Array.isArray(normalized?.yAxisKeys) ? normalized.yAxisKeys : [];
        const xKey = normalized?.xAxisKey;

        // If child chart exposed its rendered legend items (with actual colors), prefer those
        const exposed = Array.isArray(dataContext?.legendItems) ? dataContext.legendItems : [];
        if (exposed.length > 0) return exposed;

        // Line-like charts: legend from series (y-axis keys)
        if (chartType === 'line-chart') {
            const colors = Array.isArray(theme?.seriesColors) ? theme.seriesColors : [];
            return yKeys.map((k, i) => ({ label: String(k), color: colors[i] }));
        }

        // Donut charts: follow DonutChart segmentation logic
        if (chartType === 'donut-chart') {
            const keys = yKeys;
            const valueMode = theme?.valueMode || 'sum';
            const themeSegs = Array.isArray(theme?.segments) ? theme.segments : [];
            if (themeSegs.length > 0) {
                return themeSegs.map(s => ({ label: String(s.label ?? ''), color: s.color }));
            }
            const computeVal = (key) => {
                if (!Array.isArray(rows) || rows.length === 0) return 0;
                if (valueMode === 'sum') return rows.reduce((a, r) => a + (Number(r?.[key]) || 0), 0);
                if (valueMode === 'avg') {
                    const t = rows.reduce((a, r) => a + (Number(r?.[key]) || 0), 0);
                    return rows.length ? t / rows.length : 0;
                }
                const last = rows[rows.length - 1];
                return Number(last?.[key]) || 0;
            };
            if (keys.length > 1) {
                return keys.map((k, i) => ({ label: String(theme?.labels?.[i] ?? k), color: theme?.colors?.[i] }));
            }
            const yKey = keys[0];
            if (yKey && Array.isArray(rows) && rows.length > 0 && xKey) {
                return rows.map((r, i) => ({ label: String(r?.[xKey] ?? `Item ${i + 1}`), color: undefined }));
            }
        }

        // For bar and other charts, prefer not to show external legend unless explicitly configured
        return [];
    })();


    const showLegend = (() => {
        if (!legendConfig) return false;
        if (legendConfig === 'auto' || legendConfig?.auto === true) {
            return autoLegendItems.length > 0;
        }
        return Boolean(legendConfig && legendConfig.visible !== false && Array.isArray(legendConfig.items) && legendConfig.items.length > 0);
    })();

    const rightContent = (rightExtras || showLegend) ? (
        <div className={`flex items-center gap-4`}>
            {rightExtras}
            {showLegend ? (
                <div className={`flex items-center gap-4`}>
                    {(legendConfig === 'auto' || legendConfig?.auto === true
                        ? autoLegendItems
                        : (legendConfig?.items || [])
                    ).filter(i => i?.visible !== false).map((item, idx) => {
                        const label = item.labelPath ? getValueByPath(dataContext, item.labelPath) : (item.label ?? '');
                        const color = item.colorPath ? getValueByPath(dataContext, item.colorPath) : item.color;
                        if (!label) return null;
                        return (
                            <div key={item.id || idx} className={`flex items-center gap-1`}>
                                <div style={{ backgroundColor: color || '#64748b' }} className={`w-4 h-4 rounded`}></div>
                                <p className="text-sm font-medium">{label}</p>
                            </div>
                        );
                    })}
                </div>
            ) : null}
        </div>
    ) : null;

    return (
        <div className={`flex justify-between items-center mt-4`}>
            <div className={`flex items-center gap-2`}>
                {showPrimaryValue && hasPrimaryValue ? (
                    <h3 className={`mb-0 ${primaryValueTypos.className}`} style={primaryValueTypos.style}>{primaryValue}</h3>
                ) : null}

                {showPrimaryLabel && hasPrimaryLabel ? (
                    <p className={`${primaryLabelTypos.className}`} style={primaryLabelTypos.style}>{primaryLabel}</p>
                ) : null}

                {/* {showDelta ? (
                    <div className={`inline-flex gap-1 items-center rounded-md py-[6px] px-2 ${isPositive ? 'bg-green-100' : 'bg-red-100'}`}>
                        <span className={`text-base font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                            {isPositive ? '+ ' : '- '}{Math.abs(deltaNumber)}{meta.delta?.isPercent !== false ? '%' : ''}
                        </span>
                        <img src={isPositive ? ChartUpIcon : ChartDownIcon} alt="" className="h-3 w-3" />
                    </div>
                ) : null} */}

                {showDelta && deltaNumber !== 0 ? (
                    <GrowthChip
                        value={deltaNumber}
                        isPositive={isPositive}
                        isPercent={deltaCfg?.isPercent !== false}
                        description={deltaCfg?.description}
                    />
                ) : null}
            </div>

            {rightContent}
        </div>
    );
}


